import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Session, User } from "@supabase/supabase-js";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  signInWithPassword: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signUpWithEmail: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  checkEmailExists: (email: string) => Promise<boolean>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface UserRole {
  id: string;
  email: string;
  role: string;
  created_at: string;
  updated_at: string;
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          setUser(session.user);
          await checkAdminStatus(session.user.email);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        setUser(session.user);
        await checkAdminStatus(session.user.email);
      } else {
        setUser(null);
        setIsAdmin(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAdminStatus = async (email: string) => {
    try {
      setIsAdmin(email === 'can@finchnow.com');
    } catch (error) {
      console.error('Error in checkAdminStatus:', error);
      setIsAdmin(false);
    }
  };

  const signInWithPassword = async (email: string, password: string) => {
    try {
      if (!email || !password) {
        throw new Error("Email and password are required");
      }
      
      if (password.length < 6) {
        throw new Error("Password must be at least 6 characters");
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error("Sign in error:", error);
        if (error.message.includes("Invalid login credentials")) {
          return { success: false, error: "Geçersiz email veya şifre" };
        } else if (error.message.includes("Email not confirmed")) {
          return { success: false, error: "Lütfen email adresinizi onaylayın" };
        }
        return { success: false, error: error.message };
      }

      if (data.user) {
        console.log("Successfully signed in user:", data.user.email);
        await checkAdminStatus(data.user.email);
        navigate("/admin/dashboard");
        return { success: true };
      }

      return { success: false, error: "Giriş başarısız" };
    } catch (error) {
      console.error("Error in signInWithPassword:", error);
      return { success: false, error: "Giriş yapılırken bir hata oluştu" };
    }
  };

  const checkEmailExists = async (email: string) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password: "temporary-password-123",
        options: {
          emailRedirectTo: window.location.origin
        }
      });
      
      if (error) {
        if (error.message.includes("User already registered")) {
          return true;
        }
        if (error.message.includes("User not found")) {
          return false;
        }
        return true;
      }
      
      return true;
    } catch (error: any) {
      console.error("Error checking email:", error);
      return false;
    }
  };

  const signUpWithEmail = async (email: string, password: string) => {
    try {
      if (password.length < 6) {
        return { success: false, error: "Password must be at least 6 characters" };
      }
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: import.meta.env.VITE_SITE_URL || window.location.origin,
          data: {
            email,
          }
        }
      });

      if (error) {
        console.error("Sign up error:", error);
        if (error.message.includes("User already registered")) {
          return { success: false, error: "An account with this email already exists" };
        }
        if (error.message.includes("Invalid email")) {
          return { success: false, error: "Please enter a valid email address" };
        }
        throw error;
      }
      
      console.log("Sign up successful:", data.user?.email);
      toast.success("Account created successfully! Please check your email to confirm your account.");
      return { success: Boolean(data.user) };
    } catch (error: any) {
      console.error("Error signing up:", error);
      return { success: false, error: error.message || "Failed to sign up" };
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      toast.success("Signed out successfully");
    } catch (error: any) {
      console.error("Error signing out:", error);
      toast.error("Error signing out");
    }
  };

  if (loading) {
    return null;
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isAdmin,
        signInWithPassword,
        signUpWithEmail,
        signOut,
        checkEmailExists,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
